// List changes: applied to IndexedDB straight away, then queued for the server.

import type { TFunction } from 'i18next';
import { newId, type ListItem, type UserList } from '@dishly/shared';
import { db, type OutboxEntry } from './db';
import { flushOutbox } from './sync';

export function listName(list: Pick<UserList, 'kind' | 'name'>, t: TFunction): string {
  if (list.kind === 'shopping') return t('lists.shopping');
  if (list.kind === 'favourites') return t('lists.favourites');
  return list.name ?? '';
}

async function enqueue(entry: Omit<OutboxEntry, 'createdAt' | 'seq'>): Promise<void> {
  await db.outbox.add({ ...entry, createdAt: Date.now() });
  void flushOutbox();
}

async function updateLocal(listId: string, change: (list: UserList) => UserList): Promise<void> {
  await db.transaction('rw', db.lists, async () => {
    const list = await db.lists.get(listId);
    if (list) await db.lists.put({ ...change(list), updatedAt: new Date().toISOString() });
  });
}

export async function putListItem(
  listId: string,
  recipeId: string,
  changes: { servings?: number | null; collapsed?: boolean },
): Promise<void> {
  await updateLocal(listId, (list) => {
    const existing = list.items.find((item) => item.recipeId === recipeId);
    const item: ListItem = existing
      ? { ...existing, ...changes }
      : {
          recipeId,
          servings: changes.servings ?? null,
          collapsed: changes.collapsed ?? false,
          addedAt: new Date().toISOString(),
        };
    return { ...list, items: existing ? list.items.map((i) => (i.recipeId === recipeId ? item : i)) : [...list.items, item] };
  });
  await enqueue({ method: 'PUT', path: `/lists/${listId}/items/${recipeId}`, body: changes });
}

export async function removeListItem(listId: string, recipeId: string): Promise<void> {
  await updateLocal(listId, (list) => ({ ...list, items: list.items.filter((i) => i.recipeId !== recipeId) }));
  await enqueue({ method: 'DELETE', path: `/lists/${listId}/items/${recipeId}` });
}

export async function createList(name: string): Promise<string> {
  const id = newId();
  const now = new Date().toISOString();
  await db.lists.put({ id, kind: 'custom', name, items: [], createdAt: now, updatedAt: now });
  await enqueue({ method: 'POST', path: '/lists', body: { id, name } });
  return id;
}

export async function renameList(listId: string, name: string): Promise<void> {
  await updateLocal(listId, (list) => ({ ...list, name }));
  await enqueue({ method: 'PATCH', path: `/lists/${listId}`, body: { name } });
}

export async function deleteList(listId: string): Promise<void> {
  await db.lists.delete(listId);
  await db.listTicks.where('key').startsWith(`${listId}:`).delete();
  await enqueue({ method: 'DELETE', path: `/lists/${listId}` });
}
